import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { useT } from '@transifex/react';
import { useThemeVersion } from '@raisin/components-lib';
import { formatLocaleForIntl, getDistributor, isCSR, useFeatureFlag } from '@raisin/cp-utils';
import { HeaderNavigationDataContext, useHeaderDataContext } from '../../../context';
import { usePostboxUnreadCount } from '../../../hooks';
import { LogoutCountdown, UnifiedNavigation as UnifiedNavigationV1, UnifiedNavigationV2, } from '../../../UnifiedHeader';
import { countrySelectorMenu, getCorporateDataSource, getIPPDistributionPartnerDataSource, getLoggedInDefaultDataSource, getLoginDataSource, getLoginStatusDataSource, getLogoutStatusDataSource, getRaisinBankDataSource, getRegistrationDataSource, } from '../data';
const UnifiedHeader = ({ isLoggedIn }) => {
    const intl = useIntl();
    const t = useT();
    const router = useRouter();
    const themeVersion = useThemeVersion();
    const isThemeV2 = themeVersion === 'v2';
    const UnifiedNavigation = isThemeV2 ? UnifiedNavigationV2 : UnifiedNavigationV1;
    const { globalSettings, distributorInfo, isRegistration, regLocale, isLogin, isCountrySelectorPage, isAuthRequired, isOpenbanking, isCorporate, isRaisinBankNavigationEnabled, hideMainNavigation, } = useContext(HeaderNavigationDataContext);
    const regFlow = getDistributor();
    let locale;
    if (isCSR() && process.env.REACT_APP_ENV) {
        locale = regLocale;
    }
    else {
        locale = formatLocaleForIntl((router === null || router === void 0 ? void 0 : router.locale) || 'de-DE');
    }
    const { customerData, cobranding, transactionAccountData, productAccess, isHeaderDataLoading, ppDashboardData, piaProductInstances, transactionAccountError, hasDWMProduct, hasMigratedRobo, pomProducts, productsTypes, } = useHeaderDataContext();
    const [dataSource, setDataSource] = useState(null);
    const { data: unreadMessagesCount } = usePostboxUnreadCount();
    const { variation, isLoading: isFeatureFlagLoading } = useFeatureFlag();
    const shouldShowPaymentMethodScreens = variation === null || variation === void 0 ? void 0 : variation('customerShowPaymentMethodScreens', false);
    const userManagementPensionLogout = !isFeatureFlagLoading && (variation === null || variation === void 0 ? void 0 : variation('userManagementPensionLogout', false));
    const userManagementCallObsLogout = !isFeatureFlagLoading && (variation === null || variation === void 0 ? void 0 : variation('userManagementCallObsLogout', false));
    const b2cUseNewDashboard = variation === null || variation === void 0 ? void 0 : variation('b2cUseNewDashboard', false);
    const dbtShowNewMyDepositsPage = variation === null || variation === void 0 ? void 0 : variation('dbtShowNewMyDepositsPage', false);
    const currentPath = isCSR()
        ? window.location.href.replace(window.location.origin, '')
        : router.asPath;
    useEffect(() => {
        const loggedInDataSource = {
            isThemeV2,
            customer: customerData,
            cobranding,
            transactionAccountData: transactionAccountData !== null && transactionAccountData !== void 0 ? transactionAccountData : {},
            productAccess,
            globalSettings,
            ppDashboardData,
            unreadMessagesCount,
            intl,
            locale,
            shouldShowPaymentMethodScreens,
            transactionAccountError,
            hasDWMProduct,
            hasMigratedRobo,
            userManagementPensionLogout,
            isHeaderDataLoading,
            userManagementCallObsLogout,
            pomProducts,
            t,
            b2cUseNewDashboard,
            dbtShowNewMyDepositsPage,
            productsTypes,
        };
        if (isCountrySelectorPage) {
            setDataSource(countrySelectorMenu);
        }
        else if (isLogin || isOpenbanking) {
            setDataSource(getLoginDataSource(locale, isThemeV2));
        }
        else if (isRegistration) {
            setDataSource(getRegistrationDataSource(isThemeV2, regFlow, locale));
        }
        else if (isLoggedIn) {
            if (customerData) {
                setDataSource(getLoginStatusDataSource(loggedInDataSource));
            }
            else if (!dataSource || cobranding) {
                setDataSource(getLoggedInDefaultDataSource(isThemeV2, b2cUseNewDashboard, isHeaderDataLoading, regFlow, locale, loggedInDataSource.cobranding));
            }
        }
        else {
            setDataSource(getLogoutStatusDataSource(isThemeV2, distributorInfo, currentPath, locale));
        }
    }, [
        customerData,
        JSON.stringify(cobranding),
        globalSettings,
        intl,
        isLoggedIn,
        isCountrySelectorPage,
        piaProductInstances,
        ppDashboardData,
        productAccess,
        transactionAccountData,
        unreadMessagesCount,
        locale,
        shouldShowPaymentMethodScreens,
        isRegistration,
        regFlow,
        isFeatureFlagLoading,
        userManagementPensionLogout,
        userManagementCallObsLogout,
        isHeaderDataLoading,
        isOpenbanking,
        t,
    ]);
    const unAuthenticatedNavConditions = [
        isAuthRequired,
        isLoggedIn,
        isLogin,
        isCountrySelectorPage,
        isRegistration,
        isOpenbanking,
        isCorporate,
        isRaisinBankNavigationEnabled,
    ];
    if (hideMainNavigation) {
        return (React.createElement(UnifiedNavigation, { dataSource: getIPPDistributionPartnerDataSource(isThemeV2, currentPath, distributorInfo, locale) }));
    }
    if (unAuthenticatedNavConditions.every((condition) => !condition)) {
        return (React.createElement(UnifiedNavigation, { dataSource: getLogoutStatusDataSource(isThemeV2, distributorInfo, currentPath, locale) }));
    }
    if (isCorporate) {
        return React.createElement(UnifiedNavigation, { dataSource: getCorporateDataSource(isThemeV2) });
    }
    if (isRaisinBankNavigationEnabled) {
        return React.createElement(UnifiedNavigation, { dataSource: getRaisinBankDataSource(isThemeV2) });
    }
    if (!dataSource) {
        return React.createElement(UnifiedNavigation, null);
    }
    return React.createElement(UnifiedNavigation, { dataSource: dataSource });
};
export { LogoutCountdown, UnifiedHeader };
//# sourceMappingURL=index.js.map